import React, { useState } from 'react';
import { MenuItem } from '@material-ui/core';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import {
  style,
  API,
  useCurrentUser,
  useCurrentUserTracks,
  useUser,
  useTrack,
  useBalance,
} from '../../api';
import SelectInput from '../common/Inputs/SelectInput';
import Dialog, { DialogProps } from '../common/Dialog';
import Flex from '../common/Flex';
import { TextRegular } from '../common/Text';
import ImageBox from '../common/ImageBox';
import Box from '../common/Box';
import { MusicNote } from '../common/Icons';

import { TransparentInput } from '../common/Inputs';
import CentralizeButton from '../../VuexyDesign/atoms/CentralizeButton';

const RequestFeedbackDialog = ({
  onClose,
  userId,
  cost = 25,
  showNoVCoinsDialog,
  ...props
}: Omit<DialogProps, 'children'> & {
  userId: number;
  cost?: number;
  showNoVCoinsDialog: () => void;
}) => {
  const { t } = useTranslation('modals');
  const { data: user } = useUser(userId);
  const currentUser = useCurrentUser();
  const { refetch: fetchBalance, data: balance } = useBalance();
  const { data: tracks = [] } = useCurrentUserTracks();
  const [selectedTrack, setSelectedTrack] = useState<number | undefined>(
    undefined,
  );
  const [track] = useTrack(selectedTrack);
  const [message, setMessage] = useState<string>(
    `${t('modals:hello') as string}, ${user?.display_name}\n\n${t(
      'modals:askForFeedback',
    )}`,
  );
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasSufficientVCoins = Boolean(
    balance !== undefined && cost !== undefined && balance > cost,
  );

  const handleChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    setSubmitted(false);
    setSelectedTrack(event.target.value as number);
  };

  const onSubmitFeedback = () => {
    if (hasSufficientVCoins) {
      setIsLoading(true);
      if (currentUser && selectedTrack) {
        API.feedback
          .request(selectedTrack, userId, message)
          .then(() => setSuccess(true))
          .catch(() => setSuccess(false))
          .finally(() => {
            fetchBalance();
            setSubmitted(true);
            setIsLoading(false);
          });
      }
    } else {
      showNoVCoinsDialog();
    }
  };

  return (
    <Dialog
      {...props}
      onClose={onClose}
      title={
        user && (
          <Flex alignItems="center">
            <ImageBox
              mr={2}
              size="33px"
              borderRadius="100%"
              src={user.profile_photo}
              alt="hello"
            />
            {user.display_name}
          </Flex>
        )
      }
    >
      <Flex
        width="720px"
        height="100%"
        maxWidth="100%"
        flexDirection="column"
        alignItems="center"
        justifyContent={['space-between', 'center']}
        px={[3, 4]}
        pt={2}
        pb={4}
      >
        <Flex
          width="100%"
          flexDirection="column"
          alignItems={['center']}
          justifyContent={['flex-start', 'center']}
          flex={1}
        >
          <Flex
            width="100%"
            mb={3}
            border="1px solid"
            borderColor={style.grey_light_1_new}
            p={2}
            flexDirection="column"
            fontSize={2}
          >
            <TransparentInput
              as="textarea"
              disabled={success}
              rows={5}
              placeholder={t('modals:yourMessage') as string}
              value={message}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setMessage(event.target?.value as string);
              }}
            />
            <TextRegular m={0} mb={2} />
            <Box width="100%" mt={2} p={2}>
              <SelectInput
                fullWidth
                displayEmpty
                disabled={success}
                value={selectedTrack}
                onChange={handleChange}
              >
                <MenuItem>
                  <Flex alignItems="center">
                    <Box mr={2}>
                      <MusicNote size={18} />
                    </Box>
                    <TextRegular>
                      {t('modals:selectVirpps') as string}
                    </TextRegular>
                  </Flex>
                </MenuItem>
                {tracks.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Flex alignItems="center">
                      <ImageBox
                        mr={2}
                        alt="cover"
                        src={item.track_img}
                        size="30px"
                      />
                      <TextRegular fontWeight="bold">{item.title}</TextRegular>
                    </Flex>
                  </MenuItem>
                ))}
              </SelectInput>
            </Box>
          </Flex>
          {submitted && (
            <>
              <TextRegular
                mb={2}
                fontWeight="bold"
                color={!success ? style.error_new : style.success_new}
              >
                {success ? t('modals:feedbackSend') : t('modals:feedbackError')}
              </TextRegular>
            </>
          )}
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          position="relative"
          flexDirection={['column', 'row']}
          width="100%"
        >
          {success ? (
            <Link href={`/explore?genre=${track?.genre?.id}`}>
              <Flex
                width="100%"
                mt={3}
                px={3}
                py={2}
                height="auto"
                fontWeight="bold"
                alignItems="center"
                justifyContent="center"
              >
                <CentralizeButton
                  color="primary"
                  size="sm"
                  text={`${t('modals:askFeedbackFromOther')} ${
                    track?.genre?.name
                  } ${t('artists')}`}
                />
              </Flex>
            </Link>
          ) : (
            <>
              <Flex alignItems="center" mb={[3, 0]}>
                <TextRegular mr={3} fontSize={0}>
                  {t('modals:availableVcoins') as string}
                </TextRegular>
                <ImageBox
                  size="10px"
                  display="flex"
                  mr={1}
                  alt="vcoin"
                  src="/static/images/vcoin/vcoin.svg"
                />
                <TextRegular mr={3} fontSize={0}>
                  {balance}
                </TextRegular>
              </Flex>
              <Flex alignItems="center" height="40px" width={['100%', 'auto']}>
                <Flex
                  width={['100%', 'auto']}
                  textAlign="center"
                  fontWeight="bold"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CentralizeButton
                    color="primary"
                    size="sm"
                    text={t('modals:send') as string}
                    disabled={isLoading || !selectedTrack}
                    onClick={
                      !isLoading && selectedTrack ? onSubmitFeedback : undefined
                    }
                  />
                </Flex>
                <Flex
                  height="100%"
                  alignItems="center"
                  borderColor={style.grey_light_2_new}
                  p={3}
                >
                  <TextRegular mr={2} fontWeight="bold">
                    -{typeof cost !== 'undefined' ? cost : '25'}
                  </TextRegular>
                  <ImageBox
                    size="19px"
                    display="flex"
                    alt="vcoin"
                    src="/static/images/vcoin/vcoin.svg"
                  />
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Dialog>
  );
};

export default RequestFeedbackDialog;
